import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

interface AuthContextType {
  user: User | null;
  isAllowed: boolean | null;
  loading: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAllowed: null,
  loading: true,
  signIn: async () => {},
  signOut: async () => {},
});

/**
 * Returns the current authentication state and sign-in/sign-out methods.
 * @returns {AuthContextType} Auth state including user, access level, loading flag, and auth methods.
 */
export const useAuth = () => useContext(AuthContext);

/**
 * Provides Firebase authentication state to the component tree.
 * Supports a test-mode override via `window.__TEST_AUTH__` for E2E testing.
 * @param {object} props
 * @param {React.ReactNode} props.children - Child components that consume auth context.
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAllowed, setIsAllowed] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if ((window as any).__TEST_AUTH__) {
      const { user: testUser, isAllowed: testAllowed } = (window as any).__TEST_AUTH__;
      setUser(testUser);
      setIsAllowed(testAllowed ?? null);
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        try {
          await getDoc(doc(db, 'gatedContent', '_probe'));
          if (process.env.NODE_ENV === 'production') {
            const idToken = await firebaseUser.getIdToken();
            const res = await fetch('/api/auth-session', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ idToken }),
            });
            if (!res.ok) throw new Error('Session creation failed');
          }
          setIsAllowed(true);
        } catch {
          setIsAllowed(false);
        }
      } else {
        setIsAllowed(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const handleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch {
      // User closed the popup or auth failed
    }
  };

  const handleSignOut = async () => {
    await fetch('/api/auth-session-destroy', { method: 'POST' });
    await auth.signOut();
    setIsAllowed(null);
  };

  return (
    <AuthContext.Provider value={{ user, isAllowed, loading, signIn: handleSignIn, signOut: handleSignOut }}>
      {children}
    </AuthContext.Provider>
  );
};
